import {Injectable, OnDestroy} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus
} from '@azure/msal-browser';
import {filter, Subject, take, takeUntil} from 'rxjs';
import {environment} from '../../environments/environment';

/**
 * Proactively refreshes the API access token in the background before it expires.
 * This prevents the user from experiencing a delay on the first API call after
 * a period of inactivity, because the MsalInterceptor would otherwise perform
 * a synchronous acquireTokenSilent in the HTTP request path.
 *
 * Strategy:
 * - Primary: Listen for `visibilitychange` events. When the tab becomes visible
 *   again, check if the token is expired or about to expire and refresh immediately.
 *   This handles the main scenario (user returns after long inactivity) reliably,
 *   since setTimeout is throttled/paused in background tabs.
 * - Secondary: Schedule a setTimeout-based refresh as a best-effort fallback for
 *   cases where the tab remains in the foreground but the user is idle.
 * - If the silent refresh fails with InteractionRequiredAuthError, do nothing —
 *   the MsalInterceptor/MsalGuard will handle it on the next user interaction.
 */
@Injectable({providedIn: 'root'})
export class AuthTokenRefreshService implements OnDestroy {
  /** Minutes before expiry to trigger proactive refresh */
  private static readonly REFRESH_BUFFER_MINUTES = 10;

  private readonly _destroying$ = new Subject<void>();
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _currentExpiresOn: Date | null = null;
  private _refreshInFlight = false;

  private readonly _onVisibilityChange = () => this._handleVisibilityChange();

  constructor(
      private readonly _msalService: MsalService,
      private readonly _msalBroadcastService: MsalBroadcastService
  ) {
  }

  /**
   * Call once during app bootstrap (e.g. in AppComponent.ngOnInit).
   * Subscribes to MSAL events, registers visibility listener, and schedules proactive refresh.
   */
  init(): void {
    // Primary mechanism: refresh on tab re-activation
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    // Track token expiry from MSAL events
    this._msalBroadcastService.msalSubject$
    .pipe(
        filter(msg =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
    )
    .subscribe(event => {
      const result = event.payload as AuthenticationResult | null;
      if (result?.expiresOn) {
        this._currentExpiresOn = result.expiresOn;
        this._scheduleRefresh(result.expiresOn);
      }
    });

    /**
     * Attempt an initial schedule once MSAL is idle — take(1) so the cache lookup
     * runs exactly once at startup, not on every subsequent InteractionStatus.None event.
     */
    this._msalBroadcastService.inProgress$
    .pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        takeUntil(this._destroying$)
    )
    .subscribe(() => this._tryInitialRefreshSchedule());
  }

  private _handleVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const expiredOrExpiring = this._isTokenExpiredOrExpiring();

    // Tab just became visible — check if token needs refresh
    if (!this._currentExpiresOn) {
      // No known expiry yet — try to read from cache
      this._tryInitialRefreshSchedule();
      return;
    }

    if (expiredOrExpiring) {
      this._doSilentRefresh();
    }
  }

  private _isTokenExpiredOrExpiring(): boolean {
    if (!this._currentExpiresOn) {
      return false;
    }
    const bufferMs = AuthTokenRefreshService.REFRESH_BUFFER_MINUTES * 60 * 1000;
    return Date.now() >= this._currentExpiresOn.getTime() - bufferMs;
  }

  private _tryInitialRefreshSchedule(): void {
    const account = this._msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    // Attempt a cache-only silent request to read current token expiry
    this._msalService.instance.acquireTokenSilent({
      scopes: environment.msalConfig.api.resourceScopes,
      account,
      forceRefresh: false,
    }).then((result: AuthenticationResult) => {
      if (result.expiresOn) {
        this._currentExpiresOn = result.expiresOn;
        this._scheduleRefresh(result.expiresOn);
      }
    }).catch((_) => {
    });
  }

  private _scheduleRefresh(expiresOn: Date): void {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }

    const now = Date.now();
    const expiryMs = expiresOn.getTime();
    const bufferMs = AuthTokenRefreshService.REFRESH_BUFFER_MINUTES * 60 * 1000;
    const refreshAt = expiryMs - bufferMs;
    const delay = refreshAt - now;

    if (delay <= 0) {
      this._doSilentRefresh();
      return;
    }

    // Secondary mechanism: best-effort timer (may be throttled in background tabs)
    this._refreshTimer = setTimeout(() => {
      this._doSilentRefresh();
    }, delay);
  }

  private _doSilentRefresh(): void {
    if (this._refreshInFlight) {
      return;
    }

    const account = this._msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    const startTime = performance.now();

    this._refreshInFlight = true;
    this._msalService.instance.acquireTokenSilent({
      scopes: environment.msalConfig.api.resourceScopes,
      account,
      forceRefresh: true,
    }).then((result: AuthenticationResult) => {
      if (result.expiresOn) {
        this._currentExpiresOn = result.expiresOn;
        this._scheduleRefresh(result.expiresOn);
      }
    }).catch((error) => {
      const durationMs = Math.round(performance.now() - startTime);
      if (error instanceof InteractionRequiredAuthError) {
        console.warn(`[AuthTokenRefresh] Refresh: INTERACTION_REQUIRED in ${durationMs}ms — deferring to next user action.`);
      } else {
        console.error(`[AuthTokenRefresh] Refresh: FAILED in ${durationMs}ms`, error);
      }
    }).finally(() => {
      this._refreshInFlight = false;
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
